import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { X, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

// Check localStorage synchronously to prevent flash
const getInitialTourState = () => {
  try {
    const hasSeenTour = localStorage.getItem('datamark_tour_completed');
    return hasSeenTour !== 'true';
  } catch (e) {
    // localStorage might not be available (e.g., in some iframe contexts)
    return false;
  }
};

export const OnboardingTour = () => {
  // Initialize state synchronously from localStorage to prevent flicker
  const [showTour, setShowTour] = useState(getInitialTourState);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: 'Welcome to JTA AI Data Labeling! 🎉',
      description: 'Your AI-powered data labeling assistant. Let me show you how easy it is to use!',
      icon: <Sparkles className="w-12 h-12 text-purple-500" />
    },
    {
      title: 'Step 1: Create a Project 📁',
      description: 'Click "New Project" to start. Give it a name like "Customer Reviews" or "Support Tickets". That\'s it!',
      icon: <div className="text-4xl">1️⃣</div>
    },
    {
      title: 'Step 2: Upload Your Data 📤',
      description: 'Drag & drop a text file or CSV. We\'ll automatically process it. No technical knowledge needed!',
      icon: <div className="text-4xl">2️⃣</div>
    },
    {
      title: 'Step 3: Choose Labeling Mode 🏷️',
      description: 'Pick from 15+ modes like:\n• Sentiment (Positive/Negative)\n• Categories\n• Extract names/places\n• Detect spam\n...and more! Not sure? Ask our AI assistant!',
      icon: <div className="text-4xl">3️⃣</div>
    },
    {
      title: 'Step 4: AI Does the Work ⚡',
      description: 'Click "Label All" and GPT-5.1 automatically labels everything in seconds. You can review and edit any label.',
      icon: <div className="text-4xl">4️⃣</div>
    },
    {
      title: 'Step 5: Export & Done ✅',
      description: 'Download your labeled data as JSON or CSV. Ready to use for machine learning, analysis, or reports!',
      icon: <div className="text-4xl">5️⃣</div>
    },
    {
      title: 'Need Help Anytime? 💬',
      description: 'Click the chat button (bottom right) to ask our AI assistant anything. It knows the entire app!',
      icon: <CheckCircle2 className="w-12 h-12 text-green-500" />
    }
  ];

  const handleComplete = () => {
    localStorage.setItem('datamark_tour_completed', 'true');
    setShowTour(false);
  };

  const handleSkip = () => {
    localStorage.setItem('datamark_tour_completed', 'true');
    setShowTour(false);
  };

  if (!showTour) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl mx-4 bg-white border-0 rounded-3xl shadow-2xl">
        <CardHeader className="relative p-4 sm:p-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSkip}
            className="absolute right-2 top-2 sm:right-4 sm:top-4 rounded-full"
            data-testid="skip-tour-btn"
          >
            <X className="w-4 h-4" />
          </Button>
          <div className="flex flex-col items-center text-center space-y-3 sm:space-y-4 pt-4">
            {steps[currentStep].icon}
            <CardTitle className="text-xl sm:text-2xl px-4">{steps[currentStep].title}</CardTitle>
            <CardDescription className="text-sm sm:text-base whitespace-pre-line px-4">
              {steps[currentStep].description}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4 sm:space-y-6 pb-4 sm:pb-6 px-4 sm:px-6">
          {/* Progress Dots */}
          <div className="flex justify-center gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-8 bg-blue-700'
                    : index < currentStep
                    ? 'w-2 bg-blue-400'
                    : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="rounded-full h-10 px-4 text-sm"
            >
              Back
            </Button>
            
            <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
              {currentStep + 1} of {steps.length}
            </span>

            {currentStep < steps.length - 1 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="btn-modern rounded-full h-10 px-4 text-sm"
                data-testid="next-step-btn"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                className="btn-modern rounded-full h-10 px-4 text-sm bg-green-600 hover:bg-green-700"
                data-testid="complete-tour-btn"
              >
                Got It! <CheckCircle2 className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>

          {/* Skip Link */}
          {currentStep === 0 && (
            <div className="text-center pt-2">
              <button
                onClick={handleSkip}
                className="text-xs sm:text-sm text-muted-foreground hover:text-foreground underline"
              >
                I already know how to use this
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingTour;
